import { Box, Divider, ModalContent, Text } from '@modusign/design-system';
import { getLabelList } from '@modusign/openapi-client-category';
import { useDocument, useVerification } from '@modusign/react-signing';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import AgreementCheck from '@app/components/AgreementCheck';
import DocumentLabelSelectionContent from '@app/containers/Modal/EditorContractSetupPreviewModal/DocumentLabelSelectionContent';
import Title from '@app/containers/Modal/EditorContractSetupPreviewModal/Title';
import {
  contractLinkCreateSetLabels,
  getLabels,
  getMaxSigningPerParticipant,
} from '@app/features/contractLink/contractLinkCreate';
import ContractLinkAgreementCheck from '@app/features/contractLink/contractLinkCreate/types/ContractLinkAgreementCheck';
import ContractLinkAgreementCheckKey from '@app/features/contractLink/contractLinkCreate/types/ContractLinkAgreementCheckKey';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';

interface ContractLinkCreateInfoMobileContentProps {
  signerId: string;
  agreements: ContractLinkAgreementCheck;
  onCheckChange: (check: { name: ContractLinkAgreementCheckKey; value: boolean }) => void;
}

function ContractLinkCreateInfoMobileContent({
  signerId,
  agreements,
  onCheckChange,
}: ContractLinkCreateInfoMobileContentProps) {
  const workspaceId = useWorkspaceId();
  const dispatch = useDispatch();
  const maxSigningPerParticipant = useSelector(getMaxSigningPerParticipant);
  const { document } = useDocument();
  const { verification } = useVerification(signerId);
  const isMobileAuthRequired = verification?.methods.some(
    (method) => method.type === 'MOBILE_IDENTIFICATION'
  );

  const labels = useSelector(getLabels);

  const [isLabelPopoverOpen, setLabelPopoverOpen] = useState(false);

  return (
    <ModalContent mb={0} px={9}>
      <Box textAlign="initial">
        <Title>링크 이름</Title>
        <Box px={4} py={6} role="cell">
          <Text>{document.title}</Text>
        </Box>
        <Title>서명자</Title>
        <Box px={4} py={5} role="cell">
          <Text as="p" color="textNeutralVeryLight" fontSize="sm" mb={4}>
            중복 서명 허용 (1인 최대 서명 건수)
          </Text>
          <Text
            as="p"
            color={maxSigningPerParticipant ? 'textNeutralBase' : 'textNeutralVeryLight'}
            data-testid="contract-link-max-signing-per-participant"
          >
            {maxSigningPerParticipant ? `${maxSigningPerParticipant}건` : '허용 안함'}
          </Text>

          <Divider my={5} />
          <Text as="p" color="textNeutralVeryLight" fontSize="sm" mb={4}>
            인증수단
          </Text>
          <Text
            as="p"
            color={isMobileAuthRequired ? 'textNeutralBase' : 'textNeutralVeryLight'}
            data-testid="contract-link-mobile-auth-required"
          >
            {isMobileAuthRequired ? '휴대폰 본인 인증' : '설정 안함'}
          </Text>
        </Box>
      </Box>
      <Box borderBottom="1px solid" borderBottomColor="dividerNeutralBase" mt={4} pb={7}>
        <DocumentLabelSelectionContent
          isPopoverOpen={isLabelPopoverOpen}
          selectedLabels={labels}
          setIsPopoverOpen={setLabelPopoverOpen}
          setSelectedLabelIds={async (labelIds) => {
            const { labels } = labelIds?.length
              ? await getLabelList(workspaceId, { labelIds })
              : { labels: [] };
            dispatch(contractLinkCreateSetLabels(labels));
          }}
        />
      </Box>
      <Box mb={[9, 0]} mt={[8, 0]} px={[0, 8, '72px']} py={[0, 7]}>
        <AgreementCheck
          checked={agreements.linkParticipantSign}
          id="agreement-link-participant-sign"
          onClick={() =>
            onCheckChange({ name: 'linkParticipantSign', value: !agreements.linkParticipantSign })
          }
        >
          <Text color="textNeutralSlightlyLight" fontWeight="medium">
            링크로 접속한 서명자가 문서 내용을 조회하고 서명 참여할 수 있음을 동의합니다.
          </Text>
        </AgreementCheck>
        <AgreementCheck
          checked={agreements.linkUsageDeduct}
          id="agreement-link-usage-deduct"
          onClick={() =>
            onCheckChange({ name: 'linkUsageDeduct', value: !agreements.linkUsageDeduct })
          }
        >
          <Text color="textNeutralSlightlyLight" fontWeight="medium">
            서명자의 서명이 완료되면 사용량이 차감됩니다.
          </Text>
        </AgreementCheck>
      </Box>
    </ModalContent>
  );
}

export default ContractLinkCreateInfoMobileContent;
